/**
 * Icons.jsx -- AiGO Owner PWA SVG icon library
 *
 * All icons: 24×24 viewBox, 1.5-2px stroke, round caps/joins, currentColor.
 * No external dependencies. Pure inline React SVG.
 *
 * Usage:
 *   import { IconHome, IconFire } from './Icons.jsx';
 *   <IconHome size={24} className="tab-icon" />
 */

import React from 'react';

const defaults = {
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

// Shared wrapper -- applies size, className, style, and spreads extra props onto <svg>
function Svg({ size = 24, className, style, children, fill = 'none', stroke = 'currentColor', ...rest }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill}
      stroke={stroke}
      strokeWidth={defaults.strokeWidth}
      strokeLinecap={defaults.strokeLinecap}
      strokeLinejoin={defaults.strokeLinejoin}
      className={className}
      style={style}
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// LOGO -- AiGO bolt/lightning brand mark
// Angular, geometric, premium. Works at 20-40px.
// ---------------------------------------------------------------------------
export function IconLogo({ size = 24, className, style, ...rest }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      style={style}
      aria-hidden="true"
      {...rest}
    >
      {/* Outer bolt shape -- filled with primary color, no stroke */}
      <path
        d="M13.5 2L5 13.5H11.5L10.5 22L19 10.5H12.5L13.5 2Z"
        fill="var(--primary)"
        stroke="none"
      />
      {/* Inner highlight stripe -- thinner, white, creates depth */}
      <path
        d="M13 5L7.5 13H12L11.2 19L17.5 11H13L13 5Z"
        fill="white"
        fillOpacity="0.25"
        stroke="none"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// HOME -- house outline
// ---------------------------------------------------------------------------
export function IconHome({ size = 24, className, style, ...rest }) {
  return (
    <Svg size={size} className={className} style={style} {...rest}>
      <path d="M3 10.5L12 3L21 10.5V20C21 20.55 20.55 21 20 21H15V16H9V21H4C3.45 21 3 20.55 3 20V10.5Z" />
    </Svg>
  );
}

// ---------------------------------------------------------------------------
// CONVERSATIONS -- chat bubble outline
// ---------------------------------------------------------------------------
export function IconConversations({ size = 24, className, style, ...rest }) {
  return (
    <Svg size={size} className={className} style={style} {...rest}>
      <path d="M21 15C21 15.55 20.78 16.05 20.41 16.41C20.05 16.78 19.55 17 19 17H7L3 21V5C3 4.45 3.22 3.95 3.59 3.59C3.95 3.22 4.45 3 5 3H19C19.55 3 20.05 3.22 20.41 3.59C20.78 3.95 21 4.45 21 5V15Z" />
    </Svg>
  );
}

// ---------------------------------------------------------------------------
// SETTINGS -- gear/cog outline
// ---------------------------------------------------------------------------
export function IconSettings({ size = 24, className, style, ...rest }) {
  return (
    <Svg size={size} className={className} style={style} {...rest}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </Svg>
  );
}

// ---------------------------------------------------------------------------
// FIRE -- flame outline for hot leads
// ---------------------------------------------------------------------------
export function IconFire({ size = 24, className, style, ...rest }) {
  return (
    <Svg size={size} className={className} style={style} {...rest}>
      <path d="M12 2C12 2 7 7 7 12.5C7 15.54 9.24 18 12 18C14.76 18 17 15.54 17 12.5C17 10.5 16 9 16 9C16 9 15 11 13.5 11C12.5 11 12 10 12 10C12 10 14 7.5 12 2Z" />
      <path d="M12 18C12 18 10 19.5 10 21" strokeOpacity="0.5" />
      <circle cx="12" cy="21" r="1" fill="currentColor" stroke="none" />
    </Svg>
  );
}

// ---------------------------------------------------------------------------
// CALENDAR -- calendar outline for bookings
// ---------------------------------------------------------------------------
export function IconCalendar({ size = 24, className, style, ...rest }) {
  return (
    <Svg size={size} className={className} style={style} {...rest}>
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </Svg>
  );
}

// ---------------------------------------------------------------------------
// BELL -- notification bell outline
// ---------------------------------------------------------------------------
export function IconBell({ size = 24, className, style, ...rest }) {
  return (
    <Svg size={size} className={className} style={style} {...rest}>
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </Svg>
  );
}

// ---------------------------------------------------------------------------
// CHART -- bar chart outline for weekly summary
// ---------------------------------------------------------------------------
export function IconChart({ size = 24, className, style, ...rest }) {
  return (
    <Svg size={size} className={className} style={style} {...rest}>
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
      <line x1="3" y1="20" x2="21" y2="20" />
    </Svg>
  );
}

// ---------------------------------------------------------------------------
// MESSAGE -- speech bubble for empty states / save opportunities
// ---------------------------------------------------------------------------
export function IconMessage({ size = 24, className, style, ...rest }) {
  return (
    <Svg size={size} className={className} style={style} {...rest}>
      <path d="M21 15C21 15.55 20.78 16.05 20.41 16.41C20.05 16.78 19.55 17 19 17H8L3 22V5C3 4.45 3.22 3.95 3.59 3.59C3.95 3.22 4.45 3 5 3H19C19.55 3 20.05 3.22 20.41 3.59C20.78 3.95 21 4.45 21 5V15Z" />
      <line x1="8" y1="9" x2="16" y2="9" strokeOpacity="0.5" />
      <line x1="8" y1="13" x2="13" y2="13" strokeOpacity="0.5" />
    </Svg>
  );
}

// ---------------------------------------------------------------------------
// PHONE -- phone outline for SMS method
// ---------------------------------------------------------------------------
export function IconPhone({ size = 24, className, style, ...rest }) {
  return (
    <Svg size={size} className={className} style={style} {...rest}>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.41 2 2 0 0 1 3.6 1.22h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 8.9a16 16 0 0 0 6.09 6.09l.94-.94a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </Svg>
  );
}

// ---------------------------------------------------------------------------
// MAIL -- envelope outline for email method
// ---------------------------------------------------------------------------
export function IconMail({ size = 24, className, style, ...rest }) {
  return (
    <Svg size={size} className={className} style={style} {...rest}>
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </Svg>
  );
}

// ---------------------------------------------------------------------------
// ARROW LEFT -- back navigation
// ---------------------------------------------------------------------------
export function IconArrowLeft({ size = 24, className, style, ...rest }) {
  return (
    <Svg size={size} className={className} style={style} {...rest}>
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </Svg>
  );
}

// ---------------------------------------------------------------------------
// SEARCH -- magnifying glass
// ---------------------------------------------------------------------------
export function IconSearch({ size = 24, className, style, ...rest }) {
  return (
    <Svg size={size} className={className} style={style} {...rest}>
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </Svg>
  );
}

// ---------------------------------------------------------------------------
// FILTER -- funnel for conversation filters
// ---------------------------------------------------------------------------
export function IconFilter({ size = 24, className, style, ...rest }) {
  return (
    <Svg size={size} className={className} style={style} {...rest}>
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </Svg>
  );
}

// ---------------------------------------------------------------------------
// USER -- person silhouette for contact/lead
// ---------------------------------------------------------------------------
export function IconUser({ size = 24, className, style, ...rest }) {
  return (
    <Svg size={size} className={className} style={style} {...rest}>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </Svg>
  );
}

// ---------------------------------------------------------------------------
// TREND UP -- upward trend line for sparkline headers
// ---------------------------------------------------------------------------
export function IconTrendUp({ size = 24, className, style, ...rest }) {
  return (
    <Svg size={size} className={className} style={style} {...rest}>
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </Svg>
  );
}

// ---------------------------------------------------------------------------
// CHECK -- checkmark for success states
// ---------------------------------------------------------------------------
export function IconCheck({ size = 24, className, style, ...rest }) {
  return (
    <Svg size={size} className={className} style={style} {...rest}>
      <polyline points="20 6 9 17 4 12" />
    </Svg>
  );
}

// ---------------------------------------------------------------------------
// X -- close/dismiss
// ---------------------------------------------------------------------------
export function IconX({ size = 24, className, style, ...rest }) {
  return (
    <Svg size={size} className={className} style={style} {...rest}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </Svg>
  );
}

// ---------------------------------------------------------------------------
// LOCK -- padlock for security messaging
// ---------------------------------------------------------------------------
export function IconLock({ size = 24, className, style, ...rest }) {
  return (
    <Svg size={size} className={className} style={style} {...rest}>
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </Svg>
  );
}

// ---------------------------------------------------------------------------
// SEND -- paper plane for send actions
// ---------------------------------------------------------------------------
export function IconSend({ size = 24, className, style, ...rest }) {
  return (
    <Svg size={size} className={className} style={style} {...rest}>
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </Svg>
  );
}

// ---------------------------------------------------------------------------
// CHEVRON RIGHT -- right chevron for list items
// ---------------------------------------------------------------------------
export function IconChevronRight({ size = 24, className, style, ...rest }) {
  return (
    <Svg size={size} className={className} style={style} {...rest}>
      <polyline points="9 18 15 12 9 6" />
    </Svg>
  );
}

// ---------------------------------------------------------------------------
// NO SHOW -- alert triangle for no-shows
// ---------------------------------------------------------------------------
export function IconNoShow({ size = 24, className, style, ...rest }) {
  return (
    <Svg size={size} className={className} style={style} {...rest}>
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </Svg>
  );
}

// ---------------------------------------------------------------------------
// STAR -- star for engagement score
// ---------------------------------------------------------------------------
export function IconStar({ size = 24, className, style, ...rest }) {
  return (
    <Svg size={size} className={className} style={style} {...rest}>
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </Svg>
  );
}
